import { FC, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, rem, ToastContainerSyle, useBreakpointValue } from '@bonusly/design-system';

import { UserMobileNavbar } from '../../modules/user-navbar/components/user-mobile-navbar';
import { UserNavbar } from '../../modules/user-navbar/components/user-navbar';
import { GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT, GlobalMobileBottomNav } from '../action-bar/global-mobile-bottom-nav';
import { BizyFlightOverlay } from '../bizy';
import { BizySidePanelDock } from '../bizy-side-panel';
import { useBizyLayout } from '../bizy-side-panel/hooks/use-bizy-layout';
import { BugRecorderBanner, useBugMode } from '../developer/bug-recorder-banner';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { Reveal } from '../display/reveal';
import { useSeasonalTheme } from '../seasonal';
import { useSpoofing } from './hooks/useSpoofing';

export interface AppLayoutProps {
	children: ReactNode;
}

// Owned here (not in BizySidePanelDock) since this is also the sidebar's own
// width-transition duration/easing below — passed down as props so the two
// can't drift apart into hardcoded values that happen to match today.
const SIDEBAR_WIDTH_TRANSITION_MS = 200;
// Expo-out: fast start, long smooth glide to rest — snappier than Material's
// dated `cubic-bezier(.4,0,.2,1)` "standard" curve.
const SIDEBAR_TRANSITION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

export const AppLayout: FC<AppLayoutProps> = ({ children }) => {
	const { data } = useSpoofing();
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const isBugMode = useBugMode();
	const [isExpanded, setIsExpanded] = useState(true);

	const toggleSidebar = () => setIsExpanded((prev) => !prev);

	const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;

	const location = useLocation();
	const isMeetingDetailRoute = /^\/(meet\/(with\/)?[^/]+|team\/[^/]+\/meet)/.test(location.pathname);

	const bizy = useBizyLayout({ isMobile, isExpanded, setIsExpanded });

	const isSpoofing = data?.is_spoofing && !data?.is_demoing;
	const showBugBanner = isBugMode && !isSpoofing;
	const spoofingBannerHeight = rem(60);
	const mobileNavbarHeight = rem(56);
	const mobileBottomNavHeight = rem(GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT);
	const hideMobileNav = isMobile && isMeetingDetailRoute;
	const hideMobileHeader = hideMobileNav;

	const sidebarPx = isExpanded ? 260 : 60;
	const currentSidebarWidth = rem(sidebarPx);

	const getMinHeight = () => {
		if (isMobile) {
			if (hideMobileHeader) {
				return isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh';
			}
			return isSpoofing ? `calc(100vh - ${spoofingBannerHeight} - ${mobileNavbarHeight})` : `calc(100vh - ${mobileNavbarHeight})`;
		}
		return isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh';
	};

	return (
		<Box bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.surface'} minH="100vh">
			<Reveal if={isSpoofing}>
				<SpoofingWarning />
			</Reveal>
			<Reveal if={showBugBanner}>
				<BugRecorderBanner />
			</Reveal>
			<ToastContainerSyle style={{ 'padding-top': '0px' }} />

			{/* TOP NAVBAR - Mobile only, hidden on meeting detail pages */}
			<Reveal if={isMobile && !hideMobileHeader}>
				<UserMobileNavbar isSpoofing={isSpoofing} />
			</Reveal>

			<Box display="flex" minH={getMinHeight()}>
				{/* USER NAVIGATION - Desktop only. `sticky` (not `fixed`) keeps it a real
				    flex sibling, so its width stays in sync with the panel and main content. */}
				<Reveal if={!isMobile}>
					<Box
						data-component="user-navbar"
						position="sticky"
						top={isSpoofing ? spoofingBannerHeight : '0'}
						height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
						width={currentSidebarWidth}
						flexShrink={0}
						bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.canvas'}
						zIndex={11}
						transition={bizy.suppressSidebarTransition ? 'none' : `width ${SIDEBAR_WIDTH_TRANSITION_MS}ms ${SIDEBAR_TRANSITION_EASING}`}
						overflow="hidden"
					>
						<UserNavbar isExpanded={isExpanded} toggleSidebar={toggleSidebar} showBizyToggle={bizy.isDesktop} isBizyPanelOpen={bizy.isPanelOpen} />
					</Box>
				</Reveal>

				<Reveal if={bizy.isDesktop}>
					<BizySidePanelDock
						isOpen={bizy.isPanelOpen}
						isFullscreen={bizy.isFullscreen}
						isResizing={bizy.isResizing}
						panelWidth={bizy.panelWidth}
						sidebarPx={sidebarPx}
						sidebarTransitionMs={SIDEBAR_WIDTH_TRANSITION_MS}
						sidebarTransitionEasing={SIDEBAR_TRANSITION_EASING}
						top={isSpoofing ? spoofingBannerHeight : '0'}
						height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
					/>
				</Reveal>

				{/* CONTENTS — flex:1 fills whatever the sidebar/panel don't take;
				    no margin math to keep in sync, the browser does it. */}
				<Box as="main" pb={{ base: hideMobileNav ? 0 : mobileBottomNavHeight, sm: 0 }} flex="1" minW="0">
					{children}
				</Box>
			</Box>

			{/* BOTTOM NAVIGATION - Mobile only, hidden on meeting detail pages */}
			<Reveal if={isMobile && !hideMobileNav}>
				<GlobalMobileBottomNav />
			</Reveal>
			<BizyFlightOverlay />
		</Box>
	);
};
